import {
  AccountInfo,
  AuthenticationResult,
  AuthError,
  BrowserAuthError,
  EndSessionRequest,
  EventMessage,
  EventPayload,
  EventType,
  InteractionRequiredAuthError,
  PublicClientApplication,
  RedirectRequest,
  SilentRequest
} from "@azure/msal-browser";
import { ActionType, Behavior } from "@microsoft/1ds-analytics-web-js";
import { logService, SloDefinitions, TelemetryEventType } from 'npo-common';
import { extractLocale, isNullOrUndefined } from 'src/common/utilities/Utilities';
import { isLocalizationEnabled } from 'src/components/utilities/Localization/Utils';
import { history } from 'src/index';
import { loginRequest, msalConfig, tokenRequest } from "../config/msalConfig";

declare const window: any;

enum AuthenticatedStateType {
  SignedIn = '1',
  NotSignedIn = '3'
}

export class ModernAuthenticationService {
  private msalInstance: PublicClientApplication;
  private currentAccount: AccountInfo | null = null;
  private sessionLocale: any;
  private debugMode: boolean;
  private isInitialized: boolean = false;
  private initializationPromise: Promise<void> | null = null;

  constructor() {
    // Initialize localization
    if (isLocalizationEnabled()) {
      const localeFromSessionStorage = sessionStorage.getItem("locale");
      this.sessionLocale =
        !isNullOrUndefined(localeFromSessionStorage) && localeFromSessionStorage?.length == 5
          ? extractLocale(localeFromSessionStorage)
          : null;
    }

    // Update redirect URIs based on current context
    const updatedConfig = {
      ...msalConfig,
      auth: {
        ...msalConfig.auth,
        redirectUri: window.location.pathname.includes("/signup-complete")
          ? `${window.location.origin}/registration-complete`
          : window.location.origin,
        postLogoutRedirectUri: isLocalizationEnabled() && this.sessionLocale
          ? window.location.origin + "/" + this.sessionLocale + "/getting-started"
          : window.location.origin + "/getting-started"
      }
    };

    this.msalInstance = new PublicClientApplication(updatedConfig);
    this.debugMode = process.env.REACT_APP_DEBUG_MODE === "1";

    // Add event callbacks
    this.msalInstance.addEventCallback((event: EventMessage) => {
      this.handleMsalEvent(event);
    });

    // Initialize MSAL instance
    this.initializationPromise = this.initialize();
  }

  private async initialize(): Promise<void> {
    try {
      await this.msalInstance.initialize();
      this.isInitialized = true;
      
      // Initialize accounts after MSAL is ready
      this.initializeAccounts();
      this.loadMeControl();
      
      if (this.debugMode) {
        console.log('MSAL instance initialized successfully');
      }
    } catch (error) {
      logService.logError("MSAL initialization failed", error as Error);
      throw error;
    }
  }

  private async ensureInitialized(): Promise<void> {
    if (!this.isInitialized && this.initializationPromise) {
      await this.initializationPromise;
    }
  }

  private initializeAccounts(): void {
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      // Set the first account as the active account
      this.currentAccount = accounts[0];
      this.msalInstance.setActiveAccount(this.currentAccount);
    }
  }

  private handleMsalEvent(event: EventMessage): void {
    if (this.debugMode) {
      console.log('MSAL Event:', event);
    }

    switch (event.eventType) {
      case EventType.LOGIN_SUCCESS:
        if (event.payload) {
          const payload = event.payload as EventPayload;
          this.handleLoginSuccess(payload as AuthenticationResult);
        }
        break;
      case EventType.ACQUIRE_TOKEN_SUCCESS:
        if (event.payload) {
          const payload = event.payload as EventPayload;
          this.handleTokenSuccess(payload as AuthenticationResult);
        }
        break;
      case EventType.ACQUIRE_TOKEN_FAILURE:
        this.handleTokenFailure(event.error || null);
        break;
      case EventType.LOGOUT_SUCCESS:
        this.handleLogoutSuccess();
        break;
      default:
        break;
    }
  }

  private handleLoginSuccess(result: AuthenticationResult): void {
    this.currentAccount = result.account;
    this.msalInstance.setActiveAccount(this.currentAccount);

    // Analytics and telemetry
    window.analytics?.capturePageAction(null, {
      behavior: Behavior.SIGNIN,
      actionType: ActionType.CLICKLEFT,
      contentTags: {
        authenticationPlatform: "AAD"
      }
    });

    logService.logEvent({
      Name: "Signin Attempt",
      EventType: TelemetryEventType.PrimaryWorkflow,
      SloId: SloDefinitions.NonprofitSignIn,
      ButtonName: "Signin-CTA",
      Result: "Success",
    });

    // Handle state-based navigation
    if (result.state) {
      try {
        const tokenResponseState = JSON.parse(result.state);
        if (tokenResponseState.href && !history.location.pathname.includes(tokenResponseState.href)) {
          history.push(tokenResponseState.href);
        }
      } catch (error) {
        console.warn('Failed to parse state:', error);
      }
    }

    this.loadMeControl();
  }

  private handleTokenSuccess(result: AuthenticationResult): void {
    if (history.location.pathname.includes('/convert-account')) {
      history.push('/new/profile');
    }
  }

  private handleTokenFailure(error: Error | null): void {
    logService.logEvent({
      Name: "Signin Attempt",
      EventType: TelemetryEventType.PrimaryWorkflow,
      SloId: SloDefinitions.NonprofitSignIn,
      ButtonName: "Signin-CTA",
      Result: "Failure",
    });

    if (error) {
      logService.logError("Token acquisition failed", error);
    }
  }

  private handleLogoutSuccess(): void {
    this.currentAccount = null;
    sessionStorage.clear();
  }

  public async login(): Promise<void> {
    try {
      await this.ensureInitialized();
      
      const loginRequestWithState = {
        ...loginRequest,
        state: window.location.pathname.includes('/convert-account') 
          ? JSON.stringify({ href: '/new/profile' }) 
          : undefined
      };

      await this.msalInstance.loginRedirect(loginRequestWithState);
    } catch (error) {
      logService.logError("Login initiation failed", error as Error);
      throw error;
    }
  }

  public async logout(): Promise<void> {
    try {
      await this.ensureInitialized();
      
      const logoutRequest: EndSessionRequest = {
        account: this.currentAccount || undefined
      };

      await this.msalInstance.logoutRedirect(logoutRequest);
    } catch (error) {
      logService.logError("Logout failed", error as Error);
      throw error;
    }
  }

  public async handleRedirectPromise(): Promise<boolean> {
    try {
      await this.ensureInitialized();
      
      const response = await this.msalInstance.handleRedirectPromise({ navigateToLoginRequestUrl: false });
      
      if (response) {
        this.currentAccount = response.account;
        this.msalInstance.setActiveAccount(this.currentAccount);
        return true;
      }

      return this.isAuthenticated;
    } catch (error) {
      logService.logError("Handle redirect promise failed", error as Error);
      return false;
    }
  }

  public async getToken(): Promise<string | null> {
    try {
      await this.ensureInitialized();
      
      if (!this.currentAccount) {
        const accounts = this.msalInstance.getAllAccounts();
        if (accounts.length === 0) {
          return null;
        }
        this.currentAccount = accounts[0];
        this.msalInstance.setActiveAccount(this.currentAccount);
      }

      const silentRequest: SilentRequest = {
        ...tokenRequest,
        account: this.currentAccount
      };

      const response = await this.msalInstance.acquireTokenSilent(silentRequest);
      return response.accessToken;

    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        try {
          const redirectRequest: RedirectRequest = {
            ...loginRequest,
            account: this.currentAccount || undefined
          };
          await this.msalInstance.acquireTokenRedirect(redirectRequest);
          return null;
        } catch (redirectError) {
          logService.logError("Token redirect failed", redirectError as Error);
          return null;
        }
      } else if (error instanceof BrowserAuthError || error instanceof AuthError) {
        logService.logError("Token acquisition failed", error);
        return null;
      } else {
        logService.logError("Unexpected error during token acquisition", error as Error);
        return null;
      }
    }
  }

  public get accountInfo(): AccountInfo | null {
    if (!this.isInitialized) {
      return null;
    }
    return this.currentAccount || this.msalInstance.getActiveAccount();
  }

  public get isAuthenticated(): boolean {
    if (!this.isInitialized) {
      return false;
    }
    const account = this.accountInfo;
    return Boolean(account);
  }

  public async isAuthenticatedAsync(): Promise<boolean> {
    await this.ensureInitialized();
    const token = await this.getToken();
    return token !== null;
  }

  public getTokenExpirationInMinutes(token: string): number {
    if (!token) {
      return 0;
    }
    
    try {
      const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const decodedToken: { exp?: number } = JSON.parse(atob(payload));
      if (!decodedToken?.exp) {
        return 0;
      }
      
      // Same result as the previous moment.duration(...).minutes(): the minutes component, not total minutes.
      return Math.trunc((decodedToken.exp * 1000 - Date.now()) / 60000) % 60;
    } catch (error) {
      logService.logError("Failed to decode token", error as Error);
      return 0;
    }
  }

  private parseMsalAccountForMeControl() {
    const ipdType = 'aad';

    if (this.currentAccount && this.currentAccount.name) {
      const [firstName = '', lastName = ''] = this.currentAccount.name.split(' ');

      return {
        idp: ipdType,
        firstName: firstName,
        lastName: lastName,
        memberName: this.currentAccount.username,
        cid: process.env.REACT_APP_AAD_CLIENT_ID ?? '',
        authenticatedState: AuthenticatedStateType.SignedIn
      };
    }

    return {
      idp: ipdType,
      firstName: '',
      lastName: '',
      memberName: '',
      cid: '',
      authenticatedState: AuthenticatedStateType.NotSignedIn
    };
  }

  public loadMeControl(): void {
    const shellOptions = {
      meControlOptions: {
        apiGeneration: 'GEN1',
        rpData: {
          msaInfo: {
            signInUrl: 'mecontrol/signin',
            signOutUrl: 'mecontrol/signout'
          }
        },
        userData: this.parseMsalAccountForMeControl(),
        extensibleLinks: []
      }
    };

    // If msCommonShell already exists, load it
    if (window.msCommonShell) {
      window.msCommonShell.load(shellOptions);
    }
    // If msCommonShell doesn't yet exist...
    else {
      // Load the me control once msCommonShell is ready
      window.onShellReadyToLoad = function () {
        window.onShellReadyToLoad = null;
        window.msCommonShell.load(shellOptions);
      };
    }
  }

  public getMsalInstance(): PublicClientApplication {
    return this.msalInstance;
  }

  public async waitForInitialization(): Promise<void> {
    await this.ensureInitialized();
  }

  public get initialized(): boolean {
    return this.isInitialized;
  }
}

// Create singleton instance
const modernAuthenticationService = new ModernAuthenticationService();
export default modernAuthenticationService;
